import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import GlassCard from '../components/GlassCard';
import { familiesAPI } from '../services/api';

const FamiliesPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyCode, setNewFamilyCode] = useState('');
  const isAdmin = user?.user?.is_admin || user?.is_admin;

  useEffect(() => {
    loadFamilies();
  }, []);

  const loadFamilies = async () => {
    try {
      setLoading(true);
      const response = await familiesAPI.list();
      setFamilies(response.data.families || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load families');
      console.error('Error loading families:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    if (!newFamilyName.trim()) return;

    try {
      const data = { name: newFamilyName };
      if (newFamilyCode.trim()) {
        data.join_code = newFamilyCode.trim().toUpperCase();
      }
      const response = await familiesAPI.create(data);
      await loadFamilies();
      setShowCreateForm(false);
      setNewFamilyName('');
      setNewFamilyCode('');
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.error || 'Failed to create family';
      setError(errorMessage);
      console.error('Error creating family:', err);
      
      // If it's an auth error, show more helpful message
      if (err.response?.status === 401) {
        setError('Authentication required. Please sign in with Google to create families.');
      }
    }
  };

  const handleDeleteFamily = async (familyId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this family? This action cannot be undone.')) {
      return;
    }

    try {
      await familiesAPI.delete(familyId);
      await loadFamilies();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete family');
      console.error('Error deleting family:', err);
    }
  };

  if (loading) {
    return (
      <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
        <p style={{ color: theme.text.secondary }}>Loading families...</p>
      </GlassCard>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '36px', color: theme.text.primary }}>Families</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: theme.glass.border,
            background: theme.accent.primary,
            color: 'white',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          + New Family
        </button>
      </div>

      {error && (
        <GlassCard style={{ marginBottom: '24px', background: 'rgba(255, 0, 0, 0.1)', borderColor: 'rgba(255, 0, 0, 0.3)' }}>
          <p style={{ color: theme.text.primary }}>Error: {error}</p>
        </GlassCard>
      )}

      {showCreateForm && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <form onSubmit={handleCreateFamily}>
            <input
              type="text"
              value={newFamilyName}
              onChange={(e) => setNewFamilyName(e.target.value)}
              placeholder="Family name *"
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                fontSize: '16px',
                marginBottom: '12px',
              }}
            />
            <input
              type="text"
              value={newFamilyCode}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
                setNewFamilyCode(value);
              }}
              placeholder="Join Code (8 characters, optional - auto-generated if empty)"
              maxLength={8}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                fontSize: '16px',
                marginBottom: '12px',
                fontFamily: 'monospace',
                letterSpacing: '2px',
              }}
            />
            <p style={{ color: theme.text.muted, fontSize: '12px', marginBottom: '12px', marginTop: '-8px' }}>
              Leave empty to auto-generate a unique code
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewFamilyName('');
                  setNewFamilyCode('');
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: 'transparent',
                  color: theme.text.primary,
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </GlassCard>
      )}

      {families.length === 0 && !showCreateForm && (
        <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: theme.text.secondary, marginBottom: '16px' }}>
            No families yet. Create your first family!
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: theme.accent.primary,
              color: 'white',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + Create Your First Family
          </button>
        </GlassCard>
      )}

      {families.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px',
          }}
        >
          {families.map((family) => (
            <GlassCard key={family.id} style={{ position: 'relative' }}>
              <Link
                to={`/families/${family.id}`}
                style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
              >
                <h2 style={{ fontSize: '24px', marginBottom: '8px', color: theme.text.primary }}>
                  {family.name}
                </h2>
                {family.join_code && (
                  <p style={{ 
                    color: theme.accent.primary, 
                    fontSize: '14px', 
                    marginBottom: '4px',
                    fontFamily: 'monospace',
                    letterSpacing: '1px',
                    fontWeight: 600,
                  }}>
                    Code: {family.join_code}
                  </p>
                )}
                <p style={{ color: theme.text.muted, fontSize: '14px', marginBottom: '4px' }}>
                  Created: {new Date(family.created_at).toLocaleDateString()}
                </p>
                <p style={{ color: theme.text.secondary, fontSize: '14px', fontWeight: 500 }}>
                  {family.member_count || 0} {family.member_count === 1 ? 'Member' : 'Members'}
                </p>
              </Link>
              {isAdmin && (family.member_count || 0) === 0 && (
                <button
                  onClick={(e) => handleDeleteFamily(family.id, e)}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: 'rgba(255, 0, 0, 0.8)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  title="Delete family (admin only)"
                >
                  Delete
                </button>
              )}
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default FamiliesPage;

